import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import DataApi from '../../api/DataApi';
import './css/FilePreviewModal.css';

const FilePreviewModal = ({ show, onHide, fileRecord }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileName, setFileName] = useState('');
  const [mimeType, setMimeType] = useState('');

  useEffect(() => {
    if (show && fileRecord) {
      loadFilePreview();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [show, fileRecord]);

  const loadFilePreview = async () => {
    setLoading(true);
    setError(null);

    try {
      // Extract file details from record
      const fileId = fileRecord.id?.value || fileRecord.id;
      const name = fileRecord.file_name?.value || fileRecord.file_name || 'preview';
      const type = fileRecord.file_type?.value || fileRecord.file_type || '';
      const mime = fileRecord.mime_type?.value || fileRecord.mime_type || 'application/octet-stream';
      const directUrl = (fileRecord.file_url?.value || fileRecord.file_url || '').replace(/\\/g, '/');

      // Construct full filename
      const fullName = type && !name.includes('.') ? `${name}.${type}` : name;

      setFileName(fullName);
      setMimeType(mime);

      console.log('Loading preview for file:', { fileId, fileName: fullName, mimeType: mime, directUrl });

      // Prefer direct public URL if available
      if (directUrl) {
        setPreviewUrl(directUrl);
        setLoading(false);
        return;
      }

      // Fallback: fetch via download API
      const dataApi = new DataApi('attachments');
      const response = await dataApi.get(`/download/${fileId}`, {
        responseType: 'blob',
      });

      // Create blob URL
      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);

      setPreviewUrl(blobUrl);
      setLoading(false);

      console.log('File preview loaded successfully');
    } catch (err) {
      console.error('Error loading file preview:', err);
      setError('Failed to load file preview: ' + err.message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setLoading(true);
    setError(null);
    onHide();
  };

  const handleDownload = async () => {
    if (!fileRecord) return;

    try {
      const fileId = fileRecord.id?.value || fileRecord.id;
      const name = fileRecord.file_name?.value || fileRecord.file_name || 'download';
      const type = fileRecord.file_type?.value || fileRecord.file_type || '';
      const mime = fileRecord.mime_type?.value || fileRecord.mime_type || 'application/octet-stream';
      const directUrl = (fileRecord.file_url?.value || fileRecord.file_url || '').replace(/\\/g, '/');

      // Construct full filename
      const fullName = type && !name.includes('.') ? `${name}.${type}` : name;

      // Prefer direct public URL if available
      if (directUrl) {
        const link = document.createElement('a');
        link.href = directUrl;
        link.download = fullName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Fallback: fetch via download API
      const dataApi = new DataApi('attachments');
      const response = await dataApi.get(`/download/${fileId}`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fullName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up after delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      console.log('Download completed successfully');
    } catch (err) {
      console.error('Error downloading file:', err);
      alert(`Failed to download file: ${err.message}`);
    }
  };

  const getFileType = () => {
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType === 'application/pdf') return 'pdf';
      if (mimeType.includes('text/')) return 'text';
    }

    // Fallback to file extension
    const extension = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    if (['mp4', 'webm', 'ogg', 'mov'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'ogg', 'aac'].includes(extension)) return 'audio';
    if (extension === 'pdf') return 'pdf';
    if (['txt', 'csv', 'log', 'json', 'xml', 'html', 'css', 'js'].includes(extension)) return 'text';

    return 'unsupported';
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="preview-loading">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="preview-error">
          <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3">{error}</p>
          <Button variant="primary" onClick={handleDownload}>
            <i className="fas fa-download me-2"></i>
            Download File Instead
          </Button>
        </div>
      );
    }

    const fileType = getFileType();

    switch (fileType) {
      case 'image':
        return (
          <div className="preview-image-container">
            <img
              src={previewUrl}
              alt={fileName}
              className="preview-image"
              onError={() => setError('Failed to load image preview')}
            />
          </div>
        );

      case 'video':
        return (
          <div className="preview-video-container">
            <video
              src={previewUrl}
              controls
              className="preview-video"
              onError={() => setError('Failed to load video preview')}
            >
              Your browser does not support video playback.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="preview-audio-container">
            <div className="audio-player-wrapper">
              <i className="fas fa-music text-primary mb-3" style={{ fontSize: '3rem' }}></i>
              <h5>{fileName}</h5>
              <audio
                src={previewUrl}
                controls
                className="preview-audio"
                onError={() => setError('Failed to load audio preview')}
              >
                Your browser does not support audio playback.
              </audio>
            </div>
          </div>
        );

      case 'pdf':
        return (
          <div className="preview-pdf-container">
            <iframe
              src={previewUrl}
              title={fileName}
              className="preview-pdf"
              onError={() => setError('Failed to load PDF preview')}
            />
          </div>
        );

      case 'text':
        return (
          <div className="preview-text-container">
            <iframe
              src={previewUrl}
              title={fileName}
              className="preview-text"
              onError={() => setError('Failed to load text preview')}
            />
          </div>
        );

      case 'unsupported':
      default:
        return (
          <div className="preview-unsupported">
            <i className="fas fa-file text-secondary" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3">{fileName}</h5>
            <p className="text-muted">Preview not available for this file type</p>
            <Button variant="primary" onClick={handleDownload} className="mt-3">
              <i className="fas fa-download me-2"></i>
              Download File
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="file-preview-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-eye me-2"></i>
          {fileName || 'File Preview'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="file-preview-body">
        {renderPreview()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          <i className="fas fa-times me-2"></i>
          Close
        </Button>
        <Button variant="primary" onClick={handleDownload}>
          <i className="fas fa-download me-2"></i>
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilePreviewModal;
